import { ensureCodexCredentials } from './codexAuth.svelte.js'

class ApiConfig {
  constructor({baseurl, token, model, completion, signal, proxy}) {
    this.baseurl = baseurl
    this.token = token
    this.model = model
    this.completion = completion
    this.signal = signal
    this.proxy = proxy
  }
}

function proxy(apiConfig, url) {
  if (!apiConfig.proxy) return url
  const proxyUrl = import.meta.env.DEV ? 'http://127.0.0.1:8000/proxy' : '/proxy'
  return `${proxyUrl}/${encodeURIComponent(url)}`
}

function formatErrorChunk(chunk) {
  // TODO: handle outside llms.js?
  const errorText = (typeof chunk.error === "string"
    ? chunk.error : JSON.stringify(chunk.error, undefined, 2))
  return `\n# API error:\n\`\`\`json\n${errorText}\n\`\`\``
}

function joinApiPath(baseUrl, path) {
  const cleanPath = path.replace(/^\/+/, '')
  if (baseUrl.endsWith(`/${cleanPath}`)) return baseUrl
  if (baseUrl.endsWith('/')) return `${baseUrl}${cleanPath}`
  return `${baseUrl}/${cleanPath}`
}

function resolveOpenResponsesBaseUrl(baseurl) {
  if (baseurl.startsWith('https+openresponses://')) {
    return `https://${baseurl.slice('https+openresponses://'.length)}`
  }
  if (baseurl.startsWith('http+openresponses://')) {
    return `http://${baseurl.slice('http+openresponses://'.length)}`
  }
  return baseurl
}

function convertOpenAIMessageToOpenResponsesInputItems(message) {
  const textFromContent = content => {
    if (typeof content === 'string') return content
    if (!Array.isArray(content)) return ''
    return content.filter(item => item?.type === 'text').map(item => item.text || '').join('')
  }
  const role = message.role
  if (role === 'tool') {
    return [{
      type: 'function_call_output',
      call_id: message.tool_call_id,
      output: textFromContent(message.content)
    }]
  }

  const items = []
  if (role === 'assistant' && Array.isArray(message._reasoningItems)) {
    items.push(...message._reasoningItems)
  }
  if (role === 'assistant' && Array.isArray(message.tool_calls)) {
    const assistantText = textFromContent(message.content)
    if (assistantText) {
      items.push({
        type: 'message',
        role: 'assistant',
        content: [{type: 'output_text', text: assistantText}]
      })
    }
    for (const toolCall of message.tool_calls) {
      if (toolCall.type !== 'function' || !toolCall.function) continue
      items.push({
        type: 'function_call',
        call_id: toolCall.id,
        name: toolCall.function.name,
        arguments: typeof toolCall.function.arguments === 'string'
          ? toolCall.function.arguments
          : JSON.stringify(toolCall.function.arguments || {})
      })
    }
    return items
  }

  const itemRole = role === 'system' || role === 'developer' || role === 'assistant'
    ? role
    : 'user'
  items.push({
    type: 'message',
    role: itemRole,
    content: [{type: itemRole === 'assistant' ? 'output_text' : 'input_text', text: textFromContent(message.content)}]
  })
  return items
}

function convertOpenAIParamsToOpenResponses(apiConfig, modelParams) {
  const jsonData = {
    model: apiConfig.model,
    stream: true,
  }

  const passthroughKeys = [
    'temperature', 'top_p', 'presence_penalty', 'frequency_penalty',
    'parallel_tool_calls', 'reasoning', 'text', 'truncation', 'metadata',
    'include', 'instructions', 'store', 'background', 'service_tier',
    'prompt_cache_key', 'safety_identifier', 'top_logprobs', 'max_tool_calls',
  ]
  for (const key of passthroughKeys) {
    if (key in modelParams) jsonData[key] = modelParams[key]
  }
  if (modelParams.reasoning_effort !== undefined) {
    jsonData.reasoning = {...jsonData.reasoning, effort: modelParams.reasoning_effort}
  }
  if (resolveOpenResponsesBaseUrl(apiConfig.baseurl).startsWith('https://api.openai.com/v1/')) {
    jsonData.reasoning = {summary: 'auto', ...jsonData.reasoning}
  }
  jsonData.include = [...new Set([...(jsonData.include || []), 'reasoning.encrypted_content'])]
  if ('max_tokens' in modelParams) jsonData.max_output_tokens = modelParams.max_tokens

  if (modelParams.tools) {
    jsonData.tools = modelParams.tools.map(tool => {
      if (tool?.type !== 'function' || !tool.function) return tool
      return {
        type: 'function',
        name: tool.function.name,
        description: tool.function.description,
        parameters: tool.function.parameters,
        strict: tool.function.strict,
      }
    })
  }
  if (modelParams.tool_choice !== undefined) {
    if (typeof modelParams.tool_choice === 'object' && modelParams.tool_choice?.type === 'function') {
      jsonData.tool_choice = {
        type: 'function',
        name: modelParams.tool_choice.function?.name,
      }
    } else {
      jsonData.tool_choice = modelParams.tool_choice
    }
  }

  if (Array.isArray(modelParams.messages)) {
    jsonData.input = modelParams.messages.flatMap(convertOpenAIMessageToOpenResponsesInputItems)
  }

  if (apiConfig.baseurl === 'codex://') {
    const instructions = jsonData.input?.filter(item => item.role === 'system' || item.role === 'developer') || []
    jsonData.instructions = [jsonData.instructions, ...instructions.map(item => item.content.map(part => part.text || '').join(''))]
      .filter(Boolean).join('\n\n')
    jsonData.input = jsonData.input?.filter(item => item.role !== 'system' && item.role !== 'developer')
    jsonData.store = false
    const allowed = new Set(['model', 'stream', 'store', 'input', 'instructions', 'tools', 'tool_choice',
      'parallel_tool_calls', 'reasoning', 'text', 'include', 'prompt_cache_key', 'service_tier'])
    for (const key of Object.keys(jsonData)) if (!allowed.has(key)) delete jsonData[key]
  }

  return jsonData
}

async function* streamOpenResponses(apiConfig, modelParams) {
  const jsonData = convertOpenAIParamsToOpenResponses(apiConfig, modelParams)
  const isCodex = apiConfig.baseurl === 'codex://'
  let baseUrl = resolveOpenResponsesBaseUrl(apiConfig.baseurl)
  if (isCodex) baseUrl = 'https://chatgpt.com/backend-api/codex/'
  if (!baseUrl.endsWith('/')) baseUrl += '/'
  const url = joinApiPath(baseUrl, 'responses')

  const send = async (forceRefresh = false) => {
    apiConfig.signal?.throwIfAborted()
    const credentials = isCodex ? await ensureCodexCredentials(forceRefresh) : null
    apiConfig.signal?.throwIfAborted()
    try {
      return await fetch(proxy(isCodex ? {...apiConfig, proxy: location.origin !== 'http://localhost:5173'} : apiConfig, url), {
        signal: apiConfig.signal,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${credentials?.accessToken || apiConfig.token}`,
          'Content-Type': 'application/json',
          ...(isCodex ? {'ChatGPT-Account-ID': credentials.accountId, originator: 'eimi', Accept: 'text/event-stream'} : {}),
        },
        body: JSON.stringify(jsonData)
      })
    } catch (error) {
      if (isCodex && error.name === 'TypeError') throw new Error(location.origin === 'http://localhost:5173'
        ? 'Could not reach Codex. Check your connection or browser CORS restrictions.'
        : 'Could not reach the Codex proxy. Check that the Eimi backend is running and reachable.')
      throw error
    }
  }
  let response = await send()
  if (isCodex && response.status === 401) {
    await response.body?.cancel()
    response = await send(true)
  }

  if (!response.ok) {
    yield {error: await response.text()}
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const {value, done} = await reader.read()
    if (done) break

    buffer += decoder.decode(value, {stream: true})
    const lines = buffer.split('\n')
    buffer = lines.pop()

    for (const line of lines) {
      if (!line.startsWith('data:')) continue
      const data = line.slice(5).trim()
      if (!data) continue
      if (data === '[DONE]') return
      yield JSON.parse(data)
    }
  }
}

async function* openResponsesStreamResponse(apiConfig, modelParams) {
  const toolCallIndexById = {}
  const toolCallMetaById = {}
  const toolCallHasArgDeltaById = {}
  const toolCallHasArgFinalById = {}
  let nextToolCallIndex = 0

  const getToolCallIndex = itemId => {
    if (toolCallIndexById[itemId] === undefined) {
      toolCallIndexById[itemId] = nextToolCallIndex
      nextToolCallIndex += 1
    }
    return toolCallIndexById[itemId]
  }

  for await (const chunk of streamOpenResponses(apiConfig, modelParams)) {
    yield {rawChunk: chunk}
    if (chunk.error || chunk.type === 'error') {
      yield formatErrorChunk(chunk)
      continue
    }

    if (chunk.response?.usage) {
      const usage = chunk.response.usage
      yield {usage: {
        prompt_tokens: usage.input_tokens,
        completion_tokens: usage.output_tokens,
        ...(usage.input_tokens_details?.cached_tokens != null && {cached_prompt_tokens: usage.input_tokens_details.cached_tokens}),
      }}
    }

    if (chunk.type === 'response.output_text.delta' && chunk.delta) {
      yield chunk.delta
      continue
    }
    if (chunk.type === 'response.reasoning_summary_text.delta' && chunk.delta) {
      yield {thinking: chunk.delta}
      continue
    }
    if (chunk.type === 'response.output_item.done' && chunk.item?.type === 'reasoning' && chunk.item.encrypted_content) {
      yield {reasoningItem: chunk.item}
      continue
    }
    if (chunk.type === 'response.refusal.delta' && chunk.delta) {
      yield chunk.delta
      continue
    }

    if ((chunk.type === 'response.output_item.added' || chunk.type === 'response.output_item.done') && chunk.item?.type === 'function_call') {
      const itemId = chunk.item.id || chunk.item.call_id
      if (!itemId) continue
      toolCallMetaById[itemId] = {
        id: chunk.item.call_id,
        name: chunk.item.name,
      }
      const args = chunk.item.arguments
      if (chunk.type === 'response.output_item.done' && typeof args === 'string' && args && !toolCallHasArgDeltaById[itemId] && !toolCallHasArgFinalById[itemId]) {
        toolCallHasArgFinalById[itemId] = true
        yield {
          tool_call_delta: {
            index: getToolCallIndex(itemId),
            id: chunk.item.call_id,
            type: 'function',
            function: {name: chunk.item.name, arguments: args}
          }
        }
      }
      continue
    }

    if (chunk.type === 'response.function_call_arguments.delta' && chunk.delta) {
      const itemId = chunk.item_id
      if (!itemId) continue
      toolCallHasArgDeltaById[itemId] = true
      const meta = toolCallMetaById[itemId] || {}
      yield {
        tool_call_delta: {
          index: getToolCallIndex(itemId),
          id: meta.id,
          type: 'function',
          function: {name: meta.name, arguments: chunk.delta}
        }
      }
      continue
    }

    if (chunk.type === 'response.function_call_arguments.done' && chunk.arguments) {
      const itemId = chunk.item_id
      if (!itemId || toolCallHasArgDeltaById[itemId] || toolCallHasArgFinalById[itemId]) continue
      toolCallHasArgFinalById[itemId] = true
      const meta = toolCallMetaById[itemId] || {}
      yield {
        tool_call_delta: {
          index: getToolCallIndex(itemId),
          id: meta.id,
          type: 'function',
          function: {name: meta.name, arguments: chunk.arguments}
        }
      }
      continue
    }

    if (chunk.type === 'response.completed') {
      yield {finishReason: 'stop'}
      continue
    }
    if (chunk.type === 'response.incomplete') {
      yield {finishReason: 'length'}
      continue
    }
    if (chunk.type === 'response.failed') {
      yield formatErrorChunk({error: chunk.response?.error || chunk.error || chunk})
    }
  }
}

async function* streamOpenai(apiConfig, modelParams) {
  const jsonData = {
    stream: true,
    model: apiConfig.model,
    ...modelParams
  }

  let url
  if (apiConfig.baseurl && apiConfig.completion) {
    url = `${apiConfig.baseurl}completions`
  } else if (apiConfig.baseurl) {
    url = `${apiConfig.baseurl}chat/completions`
  } else if (apiConfig.completion) {
    url = 'https://api.openai.com/v1/completions'
  } else {
    url = 'https://api.openai.com/v1/chat/completions'
  }

  const needIncludeUsage = jsonData.stream && (
    url.startsWith('https://api.openai.com/v1/')
    || url.startsWith('https://api.minimax.io/v1/')
  )
  if (needIncludeUsage) {
    (jsonData['stream_options'] = jsonData['stream_options'] || {})['include_usage'] = true
  }

  // TODO: remove hacks
  if (url.startsWith('https://api.openai.com/v1/chat/completions')) {
    jsonData['max_completion_tokens'] = jsonData['max_tokens']
    delete jsonData['max_tokens']
  }

  const response = await fetch(proxy(apiConfig, url), {
    signal: apiConfig.signal,
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiConfig.token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(jsonData)
  })

  if (!response.ok) {
    yield {error: await response.text()}
    return
  }

  if (!jsonData.stream) {
    const result = await response.json()
    yield {
      choices: [{
        delta: result.choices[0].message,
        finish_reason: result.choices[0].finish_reason,
      }],
      usage: result.usage
    }
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const {value, done} = await reader.read()
    if (done) break

    buffer += decoder.decode(value, {stream: true})
    const lines = buffer.split('\n')
    buffer = lines.pop()

    for (const line of lines) {
      if (line.startsWith('data: [DONE]')) return
      if (line.startsWith('data:')) {
        yield JSON.parse(line.slice(5))
      }
    }
  }
}

export async function* openaiStreamResponse(apiConfig, modelParams, {streamProvider} = {}) {
  const stream = streamProvider
    ? streamProvider(apiConfig, modelParams)
    : streamOpenai(apiConfig, modelParams)
  const reasoningDetails = []

  for await (const chunk of stream) {
    yield {rawChunk: chunk}
    if (chunk.error) {
      yield formatErrorChunk(chunk)
      continue
    }
    const usage = chunk?.usage || chunk?.x_groq?.usage
    if (usage) {
      yield {usage: {
        prompt_tokens: usage.prompt_tokens,
        completion_tokens: usage.completion_tokens,
        ...(usage.prompt_tokens_details?.cached_tokens != null && {cached_prompt_tokens: usage.prompt_tokens_details.cached_tokens}),
      }}
    }
    try {
      const c = chunk?.choices?.[0]
      if (!c) continue

      if ('text' in c) {
        yield c.text
      } else if (c.delta?.tool_calls) { // no-stream compat
        for (const [index, tool_call_delta] of c.delta.tool_calls.entries()) {
          yield {tool_call_delta: {...tool_call_delta, index: tool_call_delta.index || index}}
        }
      } else if (c.delta?.content) {
        yield c.delta.content
      } else if (c.delta?.reasoning) {  // OpenRouter
        yield {thinking: c.delta.reasoning}
      } else if (c.delta?.reasoning_content) {  // DeepSeek
        yield {thinking: c.delta.reasoning_content}
      } else if (c.delta?.reasoning_details?.[0]?.text) { // MiniMax
        yield {thinking: c.delta.reasoning_details[0].text}
      }
      if (c.delta?.reasoning_details?.length) {
        for (const detail of c.delta.reasoning_details) {
          const existing = reasoningDetails.find(d => d.type === detail.type && d.index === detail.index && d.id === detail.id && d.format === detail.format)
          if (existing) {
            for (const [key, value] of Object.entries(detail)) {
              if (['data', 'text', 'summary'].includes(key) && typeof value === 'string') existing[key] = (existing[key] || '') + value
              else existing[key] = value
            }
          } else {
            reasoningDetails.push({...detail})
          }
        }
      }
      
      if (c.finish_reason) {
        yield {finishReason: c.finish_reason}
      }
    } catch (error) {
      yield 'Err: ' + JSON.stringify(chunk)
      throw error
    }
  }
  if (reasoningDetails.some(detail => detail.type === 'reasoning.encrypted')) yield {reasoningDetails}
}

const ANTHROPIC_API_PARAMS = [
  'max_tokens', 'messages', 'model', 'metadata', 'stop_sequences', 'stream',
  'system', 'temperature', 'thinking', 'tool_choice', 'tools', 'top_k', 'top_p',
]

function convertOpenAIToolsToAnthropic(tools) {
  if (!tools) return undefined
  return tools.map(tool => {
    if (tool.type === 'function') {
      return {
        name: tool.function.name,
        description: tool.function.description,
        input_schema: tool.function.parameters
      }
    }
    // Pass through other types (maybe Anthropic-specific server tools)
    return tool
  })
}

function convertOpenAIToolChoiceToAnthropic(toolChoice) {
  if (toolChoice === undefined) return undefined
  if (toolChoice === 'none') return 'none'
  if (toolChoice === 'auto') return 'auto'
  if (toolChoice === 'required') return 'any'
  if (typeof toolChoice === 'object' && toolChoice.type === 'function') {
    return { type: 'tool', name: toolChoice.function.name }
  }
  // fallback
  return toolChoice
}

function mapStopReason(stopReason) {
  if (stopReason === 'end_turn') return 'stop'
  if (stopReason === 'max_tokens') return 'length'
  if (stopReason === 'tool_use') return 'tool_calls'
  return stopReason
}

function convertOpenAIMessageToAnthropic(message) {
  const { role, content, tool_calls, tool_call_id } = message
  const newContent = []

  // Handle tool messages (role 'tool')
  if (role === 'tool' && tool_call_id) {
    let resultContent = ''
    if (typeof content === 'string') {
      resultContent = content
    } else if (Array.isArray(content)) {
      const textItem = content.find(item => item && item.type === 'text')
      if (textItem) resultContent = textItem.text
    }
    newContent.push({
      type: 'tool_result',
      tool_use_id: tool_call_id,
      content: resultContent
    })
    return { role: 'user', content: newContent }
  }

  // Non-tool messages
  if (typeof content === 'string') {
    newContent.push({ type: 'text', text: content })
  } else if (Array.isArray(content)) {
    for (const item of content) {
      if (item.type === 'text') {
        newContent.push({ type: 'text', text: item.text })
      } else if (item.type === 'image_url') {
        const imageUrl = item.image_url.url
        if (imageUrl.match(/^https?:/i)) {
          newContent.push({ type: 'image', source: { type: 'url', url: imageUrl } })
        } else {
          let mediaType = 'image/jpeg'
          let data = imageUrl
          const match = imageUrl.match(/^data:([^;]+);base64,(.+)$/)
          if (match) {
            mediaType = match[1]
            data = match[2]
          }
          newContent.push({ type: 'image', source: { type: 'base64', media_type: mediaType, data } })
        }
      }
      // ignore other types for now
    }
  }

  // Handle tool_calls for assistant messages
  if (role === 'assistant' && tool_calls) {
    for (const toolCall of tool_calls) {
      if (toolCall.type === 'function' && toolCall.function) {
        let input = {}
        const args = toolCall.function.arguments
        if (typeof args === 'string') {
          input = JSON.parse(args)
        } else if (args && typeof args === 'object') {
          input = args
        }
        newContent.push({
          type: 'tool_use',
          id: toolCall.id || `call_${Date.now()}_${Math.random()}`,
          name: toolCall.function.name,
          input
        })
      }
    }
  }

  // Ensure at least one content block
  if (newContent.length === 0) {
    newContent.push({ type: 'text', text: '' })
  }

  return { role, content: newContent }
}

async function* streamAnthropic(apiConfig, modelParams) {
  const jsonData = {
    stream: true,
    model: apiConfig.model,
  }
  for (const k of ANTHROPIC_API_PARAMS) {
    if (k in modelParams) {
      jsonData[k] = modelParams[k]
    }
  }

  let url
  const baseurl = apiConfig.baseurl
  if (baseurl === 'anthropic://') {
    url = 'https://api.anthropic.com/v1/messages'
  } else {
    let baseUrl
    if (baseurl.startsWith('anthropic://')) {
      baseUrl = baseurl.replace('anthropic://', 'https://')
    } else if (baseurl.startsWith('anthropic+')) {
      baseUrl = baseurl.replace('anthropic+', '')
    } else {
      baseUrl = baseurl
    }
    if (!baseUrl.endsWith('/')) baseUrl += '/'
    url = baseUrl + 'messages'
  }

  const response = await fetch(proxy(apiConfig, url), {
    signal: apiConfig.signal,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiConfig.token,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify(jsonData)
  })

  if (!response.ok) {
    yield {error: await response.text()}
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const {value, done} = await reader.read()
    if (done) break

    buffer += decoder.decode(value, {stream: true})
    const lines = buffer.split('\n')
    buffer = lines.pop()

    for (const line of lines) {
      if (line.startsWith('data:')) {
        yield JSON.parse(line.slice(5))
      }
    }
  }
}

async function* anthropicStreamResponse(apiConfig, modelParams) {
  const params = {...modelParams}

  if (params.tools) {
    params.tools = convertOpenAIToolsToAnthropic(params.tools)
  }
  if (params.tool_choice !== undefined) {
    params.tool_choice = convertOpenAIToolChoiceToAnthropic(params.tool_choice)
  }

  const systemMsgs = params.messages.filter(m => m.role === 'system')
  if (systemMsgs.length) {
    params.system = systemMsgs[0].content
    params.messages = params.messages.filter(m => m.role !== 'system')
  }

  params.messages = params.messages.map(convertOpenAIMessageToAnthropic)

  // Track active tool calls (id and name)
  const toolCalls = {}

  for await (const chunk of streamAnthropic(apiConfig, params)) {
    yield {rawChunk: chunk}
    if (chunk.type === 'error') {
      yield 'API error:\n' + JSON.stringify(chunk, undefined, 2)
      continue
    }
    if (chunk.error) {
      yield formatErrorChunk(chunk)
      continue
    }
    if (chunk.type === 'message_start') {
      const usage = chunk.message.usage
      yield {usage: {
        prompt_tokens: usage.input_tokens + (usage.cache_read_input_tokens || 0) + (usage.cache_creation_input_tokens || 0),
        ...(usage.cache_read_input_tokens != null && {cached_prompt_tokens: usage.cache_read_input_tokens}),
      }}
    }
    if (chunk.type === 'message_delta') {
      if (chunk.delta.stop_reason) {
        const finishReason = mapStopReason(chunk.delta.stop_reason)
        yield { finishReason }
      }
      if (chunk?.usage?.output_tokens) {
        yield {usage: {completion_tokens: chunk.usage.output_tokens}}
      }
    }
    
    // Handle tool use streaming
    if (chunk.type === 'content_block_start' && chunk.content_block.type === 'tool_use') {
      const { id, name } = chunk.content_block
      toolCalls[chunk.index] = { id, name }
    }
    
    if (chunk.type === 'content_block_delta' && chunk.delta.type === 'input_json_delta') {
      const tool = toolCalls[chunk.index]
      yield {
        tool_call_delta: {
          index: chunk.index,
          id: tool.id,
          type: 'function',
          function: {
            name: tool.name,
            arguments: chunk.delta.partial_json
          }
        }
      }
    }
    
    if (chunk.type === 'content_block_stop') {
      delete toolCalls[chunk.index]
    }
    
    if (chunk.type === 'content_block_delta' && chunk.delta.type === 'text_delta') {
      yield chunk.delta.text
    }
    if (chunk.type === 'content_block_delta' && chunk.delta.type === 'thinking_delta') {
      yield {thinking: chunk.delta.thinking}
    }
  }
}

async function* streamGoogle(apiConfig, modelParams) {
  const urlModel = encodeURIComponent(apiConfig.model)
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${urlModel}:streamGenerateContent?alt=sse&key=${apiConfig.token}`

  const response = await fetch(proxy(apiConfig, url), {
    signal: apiConfig.signal,
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(modelParams)
  })

  if (!response.ok) {
    yield {error: await response.text()}
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const {value, done} = await reader.read()
    if (done) break

    buffer += decoder.decode(value, {stream: true})
    const lines = buffer.split('\n')
    buffer = lines.pop()

    for (const line of lines) {
      if (line.startsWith('data:')) {
        yield JSON.parse(line.slice(5))
      }
    }
  }
}

async function* googleStreamResponse(apiConfig, modelParams) {
  const convertedData = {
    contents: [],
    generationConfig: {
      responseMimeType: "text/plain"
    },
    safetySettings: [
      {category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_NONE'},
      {category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_NONE'},
      {category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_NONE'},
      {category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_NONE'},
      {category: 'HARM_CATEGORY_CIVIC_INTEGRITY', threshold: 'BLOCK_NONE'}
    ],
  }

  // Convert OpenAI tools to Google functionDeclarations format
  if (modelParams.tools) {
    convertedData.tools = [{
      functionDeclarations: modelParams.tools.map(tool => ({
        name: tool.function.name,
        description: tool.function.description,
        parameters: tool.function.parameters
      }))
    }]
  }

  let messages = modelParams.messages
  const systemMsgs = messages.filter(m => m.role === 'system')

  if (systemMsgs.length) {
    convertedData.systemInstruction = {parts: [{text: systemMsgs[0].content}]}
    messages = messages.filter(m => m.role !== 'system')
  }

  for (const msg of messages) {
    const parts = []
  
    if (typeof msg.content === 'string') {
      parts.push({ text: msg.content })
    } else if (Array.isArray(msg.content)) {
      for (const item of msg.content) {
        if (item.type === 'text') {
          parts.push({ text: item.text })
        } else if (item.type === 'image_url') {
          const imageUrl = item.image_url.url
          if (imageUrl.match(/^https?:/i)) {
            // TODO: seems like Gemini only supports uploaded files, `gs://`
            parts.push({file_data: {mime_type: 'image/jpeg', file_uri: imageUrl}})
          } else {
            let mimeType = 'image/jpeg'
            let data = imageUrl
            const match = imageUrl.match(/^data:([^;]+);base64,(.+)$/)
            if (match) {
              mimeType = match[1]
              data = match[2]
            }
            parts.push({ inline_data: { mime_type: mimeType, data: data } })
          }
        }
      }
    }

    if (msg.role === 'tool') {
      let functionName = 'unknown_function'
      
      // Look back in original messages to find the tool call with matching ID
      for (let i = messages.indexOf(msg) - 1; i >= 0; i--) {
        const prevMsg = messages[i]
        if (prevMsg.role === 'assistant' && prevMsg.tool_calls) {
          const matchingCall = prevMsg.tool_calls.find(call => call.id === msg.tool_call_id)
          if (matchingCall && matchingCall.function && matchingCall.function.name) {
            functionName = matchingCall.function.name
            break
          }
        }
      }
      
      // Handle content that might be an array or string
      let content = msg.content
      if (Array.isArray(content)) {
        const textItem = content.find(item => item.type === 'text')
        content = textItem ? textItem.text : content
      }

      let toolResponse
      try {
        toolResponse = JSON.parse(content)
      } catch (e) {
        toolResponse = {result: content}  // str
      }
      
      convertedData.contents.push({
        role: 'user',
        parts: [{
          functionResponse: {
            name: functionName,
            response: toolResponse
          }
        }]
      })
    } else {
      if (msg.role === 'assistant' && msg.tool_calls) {
        for (const toolCall of msg.tool_calls) {
          if (toolCall.type === 'function' && toolCall.function) {
            parts.push({
              functionCall: {
                name: toolCall.function.name,
                args: JSON.parse(toolCall.function.arguments)
              }
            })
          }
        }
      }
      
      convertedData.contents.push({
        role: msg.role === 'assistant' ? 'model' : msg.role,
        parts,
      })
    }
  }

  if (modelParams.temperature != null) {
    convertedData.generationConfig.temperature = modelParams.temperature
  }
  if (modelParams.max_tokens != null) {
    convertedData.generationConfig.maxOutputTokens = modelParams.max_tokens
  }

  let toolCallIndex = 0
  for await (const chunk of streamGoogle(apiConfig, convertedData)) {
    yield {rawChunk: chunk}
    if (chunk.error) {
      yield formatErrorChunk(chunk)
      continue
    }
    yield {usage: {
      prompt_tokens: chunk.usageMetadata.promptTokenCount,
      completion_tokens: chunk.usageMetadata.candidatesTokenCount,
      ...(chunk.usageMetadata.cachedContentTokenCount != null && {cached_prompt_tokens: chunk.usageMetadata.cachedContentTokenCount}),
    }}
    
    // Handle function calls in the response
    const candidate = chunk.candidates?.[0]
    if (candidate?.content?.parts) {
      for (const part of candidate.content.parts) {
        if (part.functionCall) {
          // Convert Google functionCall to OpenAI tool_call format
          const functionCall = part.functionCall
          const toolCall = {
            id: `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: 'function',
            function: {
              name: functionCall.name,
              arguments: JSON.stringify(functionCall.args || {})
            }
          }
          
          // Emit OpenAI-compatible tool call delta events
          yield {
            tool_call_delta: {
              index: toolCallIndex,
              ...toolCall
            }
          }
          toolCallIndex++
        } else if (part.text) {
          yield part.text
        }
      }
    }
  }
}

function preprocessModelParams(apiConfig, modelParams) {
  if (apiConfig.baseurl === 'https://api.deepseek.com/v1/' && 'reasoning_effort' in modelParams) {
    modelParams.thinking = {
      type: 'enabled',
      reasoning_effort: modelParams.reasoning_effort
    }
    delete modelParams.reasoning_effort
  }
}

function preprocessMessages(messages, apiConfig) {
  if (apiConfig.baseurl.startsWith('https://openrouter.ai/api/v1/')) {
    return messages.map(message => {
      if (message.role !== 'assistant' || !message._reasoningDetails?.length) return message
      return {...message, reasoning_details: message._reasoningDetails}
    })
  }
  if (apiConfig.baseurl === 'https://api.minimax.io/v1/') {
    return messages.map((message, index) => {
      if (!(message.role === 'assistant' && message._thinking)) return message
      return {
        ...message,
        reasoning_details: [
          {
            type: 'reasoning.text',
            id: `reasoning-text-1`,
            format: 'MiniMax-response-v1',
            index: 0,
            text: message._thinking,
          },
        ]
      }
    })
  }
  
  if (apiConfig.baseurl === 'https://api.deepseek.com/v1/') {
    return messages.map((message, index) => {
      // if (message.role !== 'assistant' || !(message.tool_calls && message.tool_calls.length > 0)) return message
      if (message.role !== 'assistant') return message
      return {
        ...message,
        reasoning_content: message._thinking || '',
      }
    })
  }
  
  if (apiConfig.baseurl === 'https://api.z.ai/api/paas/v4/' ||
      apiConfig.baseurl === 'https://api.z.ai/api/coding/paas/v4/') {
    return messages.map(message => {
      if (message.role !== 'assistant' || !message._thinking) return message
      return {
        ...message,
        reasoning_content: message._thinking,
      }
    })
  }
  
  return messages
}

export function runLlmApi(data) {
  const apiConfig = new ApiConfig({
    signal: data.signal,
    proxy: data.proxy,
    baseurl: data.baseurl,
    token: data.token,
    model: data.model,
    completion: data.completion,
  })

  const modelParams = {...data.parameters}
  preprocessModelParams(apiConfig, modelParams)
  const useResponses = apiConfig.baseurl === 'codex://' || apiConfig.baseurl.startsWith('https+openresponses://') ||
    apiConfig.baseurl.startsWith('http+openresponses://') ||
    (apiConfig.baseurl === 'https://api.openai.com/v1/' && !apiConfig.completion &&
      (modelParams.reasoning_effort !== undefined || modelParams.reasoning !== undefined))

  if (apiConfig.completion) {
    modelParams.prompt = data.messages.map(m => {
      if (typeof m.content === 'string') return m.content
      if (Array.isArray(m.content)) {
        return m.content.map(item => item.type === 'text' ? item.text : '').join('')
      }
      return ''
    }).join('')
  } else {
    const messages = data.messages.map(message => {
      if (message._generation?.api === data.api && message._generation?.model === data.model) return message
      return {...message, _reasoningItems: undefined, _reasoningDetails: undefined}
    })
    const processedMessages = preprocessMessages(messages, apiConfig)
    modelParams.messages = processedMessages.map(message => {
      // eslint-disable-next-line no-unused-vars
      let {_id, _thinking, _generation, _reasoningDetails, _reasoningItems, ...m} = message
      if (useResponses) m._reasoningItems = _reasoningItems
      if (!Array.isArray(m.content)) return m
      if (!m.content.every(item => item.type === 'text')) return m
      return {  // DeepSeek compat
        ...m, content: m.content.map(item => item.text).join('')
      }
    })
  }

  if (apiConfig.baseurl === 'google://') {
    return googleStreamResponse(apiConfig, modelParams)
  }

  if (useResponses) {
    return openResponsesStreamResponse(apiConfig, modelParams)
  }

  if (apiConfig.baseurl.startsWith('anthropic://') || apiConfig.baseurl.startsWith('anthropic+')) {
    return anthropicStreamResponse(apiConfig, modelParams)
  }

  return openaiStreamResponse(apiConfig, modelParams)
}

window._runLlmApi = runLlmApi
